import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { updateLinksPuzzle } from '../../api/links';
import { boardToForm, formToBoard, SOFT_HYPHEN_KEY } from './linksPuzzles';
import './links.css';

// Edit a board's themes, terms and glosses. The server re-checks the
// whole board (4x4, no word twice, every term in its course's text) and
// lists every problem; a scheduled puzzle stays scheduled. A save with
// warnings (a long word without a soft hyphen) stays open to show them.
export default function LinksEditModal({ puzzle, token, onClose }) {
    const [groups, setGroups] = useState([]);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState([]);
    const [saved, setSaved] = useState(null);

    useEffect(() => {
        if (puzzle) {
            setGroups(boardToForm(puzzle));
            setErrors([]);
            setSaved(null);
            setSaving(false);
        }
    }, [puzzle]);

    const setTheme = (gi, value) => setGroups(prev => prev.map((g, i) => (i === gi ? { ...g, theme: value } : g)));
    const setTerm = (gi, ti, field, value) => setGroups(prev => prev.map((g, i) => (i !== gi ? g : {
        ...g, terms: g.terms.map((t, j) => (j === ti ? { ...t, [field]: value } : t)),
    })));

    const save = async () => {
        setSaving(true);
        setErrors([]);
        try {
            const result = await updateLinksPuzzle(token, puzzle.id, formToBoard(groups));
            if (!result.warnings?.length) {
                onClose(result);
                return;
            }
            setSaved(result);
        } catch (err) {
            setErrors(err.errors?.length ? err.errors : [err.message]);
        }
        setSaving(false);
    };

    const footer = (
        <div className="lk-modal-footer">
            <button type="button" className="lk-btn" onClick={() => onClose(saved)} disabled={saving}>
                {saved ? 'Close' : 'Cancel'}
            </button>
            <button type="button" className="lk-btn lk-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
            </button>
        </div>
    );

    return (
        <Dialog
            header={puzzle ? `Edit puzzle #${puzzle.id}` : ''}
            visible={!!puzzle}
            onHide={() => onClose(saved)}
            footer={footer}
            style={{ width: '860px' }}
            className="lk-dialog"
        >
            <p className="lk-help">
                Type terms as the course writes them; the app shows tiles in capitals. Put
                {' '}<code>{SOFT_HYPHEN_KEY}</code>{' '}where a long word may break across tile lines
                (a soft hyphen), e.g. <code>mito{SOFT_HYPHEN_KEY}chon{SOFT_HYPHEN_KEY}dria</code>. Words of eight
                letters or more need one, or the tile overflows.
            </p>
            {saved?.warnings?.length > 0 && (
                <ul className="lk-warnings">
                    {saved.warnings.map(w => <li key={w}>Saved, but {w}</li>)}
                </ul>
            )}
            {errors.length > 0 && (
                <ul className="lk-errors">{errors.map(e => <li key={e}>{e}</li>)}</ul>
            )}
            {groups.map((group, gi) => (
                <fieldset className="lk-edit-group" key={`${group.course_id}-${gi}`}>
                    <legend>{group.course_name}</legend>
                    <label className="lk-label" htmlFor={`lk-theme-${gi}`}>Theme</label>
                    <input
                        id={`lk-theme-${gi}`}
                        className="lk-input"
                        value={group.theme}
                        onChange={e => setTheme(gi, e.target.value)}
                    />
                    {group.terms.map((t, ti) => (
                        <div className="lk-edit-term" key={ti}>
                            <input
                                aria-label={`Group ${gi + 1} term ${ti + 1}`}
                                className="lk-input lk-input-term"
                                value={t.term}
                                onChange={e => setTerm(gi, ti, 'term', e.target.value)}
                            />
                            <input
                                aria-label={`Group ${gi + 1} gloss ${ti + 1}`}
                                className="lk-input"
                                value={t.gloss}
                                onChange={e => setTerm(gi, ti, 'gloss', e.target.value)}
                            />
                        </div>
                    ))}
                </fieldset>
            ))}
        </Dialog>
    );
}

LinksEditModal.propTypes = {
    puzzle: PropTypes.shape({ id: PropTypes.number, groups: PropTypes.array }),
    token: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};

LinksEditModal.defaultProps = { puzzle: null };
